import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  CalendarIcon,
  Edit,
  PlusCircle,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { format, parse, isAfter, isBefore } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useTermination } from "@/contexts/TerminationContext";
import { TerminationReason } from "@/types/termination";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import WorkingHoursSchedule from "./WorkingHoursSchedule";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface JobEntry {
  id: number;
  jobEntryPeriodStartDate: string;
  jobEntryExpirationDate: string | null;
  salary: number;
  salaryUnit: "Yearly" | "Monthly" | "Weekly" | "Hourly";
  paySchedule: "Monthly" | "Weekly";
}

/**
 * Interface for the job timeline API response
 */
interface JobTimelineResponse {
  timeline: JobEntry[];
}

/**
 * Interface for the job timeline update API response
 */
interface UpdateJobTimelineResponse {
  success: boolean;
  message?: string;
}

interface JobInformationProps {
  employeeId?: string;
  onJobCreated?: () => void;
}

const jobEntrySchema = z.object({
  jobEntryPeriodStartDate: z.string(),
  jobEntryExpirationDate: z.string().nullable(),
  salary: z.union([z.number(), z.string()]).transform((val) => {
    if (typeof val === 'string') {
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    }
    return val;
  }).refine((val) => val >= 0, "Salary must be greater than or equal to 0"),
  salaryUnit: z.enum(["Yearly", "Monthly", "Weekly", "Hourly"]),
  paySchedule: z.enum(["Monthly", "Weekly"]),
});

type JobEntryFormValues = z.infer<typeof jobEntrySchema>;

const JobInformation: React.FC<JobInformationProps> = ({
  employeeId,
  onJobCreated,
}) => {
  const [jobTimeline, setJobTimeline] = useState<JobEntry[]>([]);
  const [originalTimeline, setOriginalTimeline] = useState<JobEntry[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JobEntry | null>(null);
  const [finalStartDate, setFinalStartDate] = useState<string | null>(null);
  const [finalEndDate, setFinalEndDate] = useState<string | null>(null);
  const [activeTermination, setActiveTermination] = useState<boolean>(false);
  const [isCheckingTermination, setIsCheckingTermination] = useState<boolean>(false);

  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { employeeId: employeeIdFromParams } = useParams<{
    employeeId: string;
  }>();
  const effectiveEmployeeId = employeeId || employeeIdFromParams;

  // Get termination context functions using the hook
  const { hasActiveTermination, createTermination } = useTermination();

  const [searchParams] = useSearchParams();

  // Check for userId param in URL which would be set when a superuser is accessing the page
  useEffect(() => {
    const userIdParam = searchParams.get("userId");
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
  }, [searchParams, isSuperUserAccount]);

  useEffect(() => {
    if (effectiveEmployeeId) {
      fetchJobTimeline();
      checkActiveTermination();
    }
  }, [effectiveEmployeeId, selectedUserId]);

  useEffect(() => {
    const hasChanges =
      JSON.stringify(originalTimeline) !== JSON.stringify(jobTimeline);
    setHasUnsavedChanges(hasChanges);
  }, [jobTimeline, originalTimeline]);

  useEffect(() => {
    updateFinalDates();
  }, [jobTimeline]);

  // Check for active termination
  const checkActiveTermination = async () => {
    if (!effectiveEmployeeId) return;
    
    setIsCheckingTermination(true);
    try {
      const hasActive = await hasActiveTermination(effectiveEmployeeId);
      setActiveTermination(hasActive);
    } catch (error) {
      console.error("Error checking active termination:", error);
    } finally {
      setIsCheckingTermination(false);
    }
  };

  const fetchJobTimeline = async () => {
    if (!effectiveEmployeeId) {
      console.error("Employee ID is required");
      return;
    }

    try {
      const response = await ApiService.request<JobEntry[]>(
        EndpointKeys.GET_JOB_TIMELINE,
        {
          urlParams: {
            employeeId: effectiveEmployeeId,
          },
          // Include userId in queryParams if superuser is accessing another user's employee
          ...(isSuperUserAccount &&
            selectedUserId !== "current" && {
              queryParams: { userId: selectedUserId },
            }),
        },
      );
      if (response) {
        setJobTimeline(response);
        setOriginalTimeline(response);
      }
      updateFinalDates();
    } catch (error) {
      console.error("Error fetching job timeline:", error);
      toast({
        title: "Error",
        description: "Failed to fetch job timeline",
        variant: "destructive",
      });
    }
  };

  // Function to access existing termination
  const handleAccessTermination = () => {
    if (!effectiveEmployeeId) return;
    
    if (isSuperUserAccount && selectedUserId !== "current") {
      navigate(`/terminate/${effectiveEmployeeId}?userId=${selectedUserId}`);
    } else {
      navigate(`/terminate/${effectiveEmployeeId}`);
    }
  };

  // Function to check and potentially create a termination
  const checkAndInitiateTermination = async (
    employeeId: string,
    entryWithEndDate: JobEntry,
  ) => {
    if (!employeeId) return;

    try {
      // Check if employee already has an active termination
      const hasActive = await hasActiveTermination(employeeId);

      if (!hasActive && entryWithEndDate.jobEntryExpirationDate) {
        // Show confirmation dialog
        const shouldProceed = window.confirm(
          "An end date has been added to the employment. Would you like to start the termination process?",
        );

        if (shouldProceed) {
          // Create termination record with basic details
          await createTermination({
            employee_id: employeeId,
            termination_date: entryWithEndDate.jobEntryExpirationDate,
            final_working_day: entryWithEndDate.jobEntryExpirationDate,
            termination_reason: TerminationReason.RESIGNATION, // Default reason, can be changed by user later
          });

          // Show success toast
          toast({
            title: "Termination process started",
            description: "You will be redirected to the termination flow.",
            duration: 5000,
          });

          // Navigate to termination page - using the correct route
          if (isSuperUserAccount && selectedUserId !== "current") {
            navigate(`/terminate/${employeeId}?userId=${selectedUserId}`);
          } else {
            navigate(`/terminate/${employeeId}`);
          }
        }
      }
    } catch (error) {
      console.error("Error checking/initiating termination:", error);
      toast({
        title: "Error",
        description: "Failed to start termination process. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleSubmitTimeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveEmployeeId) {
      console.error("Employee ID is required");
      return;
    }

    try {
      await ApiService.request<UpdateJobTimelineResponse>(
        EndpointKeys.UPDATE_JOB_TIMELINE,
        {
          method: "POST",
          body: jobTimeline,
          urlParams: {
            employeeId: effectiveEmployeeId,
          },
          // Include userId in queryParams if superuser is accessing another user's employee
          ...(isSuperUserAccount &&
            selectedUserId !== "current" && {
              queryParams: { userId: selectedUserId },
            }),
        },
      );

      setHasUnsavedChanges(false);
      toast({
        title: "Success",
        description: "Job timeline updated successfully",
      });

      // Check if the most recent job entry has an end date
      if (jobTimeline.length > 0) {
        const sortedTimeline = [...jobTimeline].sort((a, b) => {
          const dateA = new Date(a.jobEntryPeriodStartDate);
          const dateB = new Date(b.jobEntryPeriodStartDate);
          return dateB.getTime() - dateA.getTime(); // Sort in descending order (newest first)
        });

        const mostRecentEntry = sortedTimeline[0];
        if (
          mostRecentEntry &&
          mostRecentEntry.jobEntryExpirationDate &&
          effectiveEmployeeId
        ) {
          // Check and potentially initiate termination
          await checkAndInitiateTermination(
            effectiveEmployeeId,
            mostRecentEntry,
          );
        }
      }

      if (onJobCreated) {
        onJobCreated();
      }

      setOriginalTimeline(jobTimeline);
      fetchJobTimeline();
    } catch (error) {
      console.error("Error updating job timeline:", error);
      toast({
        title: "Error",
        description: "Failed to update job timeline",
        variant: "destructive",
      });
    }
  };

  const form = useForm<JobEntryFormValues>({
    resolver: zodResolver(jobEntrySchema),
    defaultValues: {
      jobEntryPeriodStartDate: "",
      jobEntryExpirationDate: null,
      salary: 0,
      salaryUnit: "Yearly",
      paySchedule: "Monthly",
    },
  });

  const handleEditEntry = (entry: JobEntry) => {
    setEditingEntry(entry);
    form.reset({
      jobEntryPeriodStartDate: entry.jobEntryPeriodStartDate,
      jobEntryExpirationDate: entry.jobEntryExpirationDate,
      salary: typeof entry.salary === 'string' ? parseFloat(entry.salary) || 0 : entry.salary,
      salaryUnit: entry.salaryUnit,
      paySchedule: entry.paySchedule,
    });
    setIsEditing(true);
  };

  // Convert date to ISO format for API and internal use
  const formatDateToISO = (dateString: string) => {
    // Parse British format date (dd/MM/yyyy) to Date object
    const parsedDate = parse(dateString, "dd/MM/yyyy", new Date());
    return parsedDate.toISOString().split("T")[0];
  };

  // Convert ISO date to British format for display
  const formatDateToBritish = (isoDate: string) => {
    return format(new Date(isoDate), "dd/MM/yyyy");
  };

  const validateEntry = (entry: JobEntry, index: number): boolean => {
    const prevEntry = jobTimeline[index - 1];
    const nextEntry = jobTimeline[index + 1];

    // Check for pay schedule change validation
    if (
      prevEntry &&
      prevEntry.paySchedule !== entry.paySchedule &&
      new Date(entry.jobEntryPeriodStartDate).getDate() !== 1
    ) {
      toast({
        title: "Invalid Entry",
        description:
          "You can only change the pay schedule on the first of the month",
        variant: "destructive",
      });
      return false;
    }

    // Check for chronological order with previous entry
    if (prevEntry) {
      const prevEndDate = prevEntry.jobEntryExpirationDate
        ? new Date(prevEntry.jobEntryExpirationDate)
        : new Date(prevEntry.jobEntryPeriodStartDate);
      const currentStartDate = new Date(entry.jobEntryPeriodStartDate);

      // Add one day to the previous end date
      const expectedStartDate = new Date(prevEndDate);
      expectedStartDate.setDate(expectedStartDate.getDate() + 1);

      // Compare the dates ignoring time
      if (
        currentStartDate.toISOString().split("T")[0] !==
        expectedStartDate.toISOString().split("T")[0]
      ) {
        toast({
          title: "Invalid Entry",
          description:
            "There must be no gaps between job entries. Each entry should start the day after the previous entry ends.",
          variant: "destructive",
        });
        return false;
      }
    }

    // Check for overlap with next entry
    if (nextEntry) {
      const currentEndDate = entry.jobEntryExpirationDate
        ? new Date(entry.jobEntryExpirationDate)
        : new Date(entry.jobEntryPeriodStartDate);
      const nextStartDate = new Date(nextEntry.jobEntryPeriodStartDate);

      // Add one day to the current end date to check for continuity
      const expectedNextStartDate = new Date(currentEndDate);
      expectedNextStartDate.setDate(expectedNextStartDate.getDate() + 1);

      // Compare the dates ignoring time
      if (
        nextStartDate.toISOString().split("T")[0] !==
        expectedNextStartDate.toISOString().split("T")[0]
      ) {
        toast({
          title: "Invalid Entry",
          description:
            "There must be no gaps between job entries. Each entry should start the day after the previous entry ends.",
          variant: "destructive",
        });
        return false;
      }
    }

    // Validate that expiration date is after start date
    if (entry.jobEntryExpirationDate) {
      const startDate = new Date(entry.jobEntryPeriodStartDate);
      const endDate = new Date(entry.jobEntryExpirationDate);

      if (isBefore(endDate, startDate)) {
        toast({
          title: "Invalid Entry",
          description:
            "Job entry expiration date cannot be before the start date.",
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const updateFinalDates = () => {
    if (jobTimeline.length > 0) {
      const sortedTimeline = [...jobTimeline].sort(
        (a, b) =>
          new Date(a.jobEntryPeriodStartDate).getTime() -
          new Date(b.jobEntryPeriodStartDate).getTime(),
      );
      setFinalStartDate(sortedTimeline[0].jobEntryPeriodStartDate);
      const lastEntry = sortedTimeline[sortedTimeline.length - 1];
      setFinalEndDate(lastEntry.jobEntryExpirationDate || null);
    } else {
      setFinalStartDate(null);
      setFinalEndDate(null);
    }
  };

  const handleUpdateEntry = (values: JobEntryFormValues) => {
    const newEntry: JobEntry = {
      ...editingEntry!,
      ...values,
    };

    const index = jobTimeline.findIndex(
      (entry) => entry.id === editingEntry!.id,
    );

    if (validateEntry(newEntry, index)) {
      setJobTimeline((timeline) =>
        timeline.map((entry) =>
          entry.id === editingEntry!.id ? newEntry : entry,
        ),
      );
      setIsEditing(false);
      setEditingEntry(null);
    }
  };

  const handleAddEntry = () => {
    const lastEntry = jobTimeline[jobTimeline.length - 1];
    const newStartDate = lastEntry
      ? new Date(
          lastEntry.jobEntryExpirationDate || lastEntry.jobEntryPeriodStartDate,
        )
      : new Date();
    newStartDate.setDate(newStartDate.getDate() + 1); // Set to the next day

    const newEntry: JobEntry = {
      id: Date.now(),
      jobEntryPeriodStartDate: newStartDate.toISOString().split("T")[0],
      jobEntryExpirationDate: null,
      salary: 0,
      salaryUnit: "Yearly",
      paySchedule: "Monthly",
    };

    if (validateEntry(newEntry, jobTimeline.length)) {
      setJobTimeline([...jobTimeline, newEntry]);
    }
  };

  const handleRemoveEntry = (id: number) => {
    setJobTimeline((timeline) => timeline.filter((entry) => entry.id !== id));
  };

  return (
    <div>
      {hasUnsavedChanges && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Please submit the timeline for changes to
            take effect.
          </AlertDescription>
        </Alert>
      )}

      {/* Active Termination Alert */}
      {activeTermination && (
        <Alert className="mb-4 border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-orange-800">
              This employee has an active termination process in progress.
            </span>
            <Button
              onClick={handleAccessTermination}
              variant="outline"
              size="sm"
              className="ml-4 border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              Access Termination
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-4 p-4 bg-gray-100 rounded-md mt-6">
        <h3 className="text-lg font-semibold mb-2">Employment Period</h3>
        <p>
          Start Date:{" "}
          {finalStartDate
            ? formatDateToBritish(finalStartDate + "T00:00:00")
            : "Not set"}
        </p>
        <p>
          End Date:{" "}
          {finalEndDate ? formatDateToBritish(finalEndDate) : "Ongoing"}
        </p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job Entry Period Start Date</TableHead>
            <TableHead>Job Entry Expiration Date</TableHead>
            <TableHead>Salary</TableHead>
            <TableHead>Salary Unit</TableHead>
            <TableHead>Pay Schedule</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobTimeline.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>
                {formatDateToBritish(
                  entry.jobEntryPeriodStartDate + "T00:00:00",
                )}
              </TableCell>
              <TableCell>
                {entry.jobEntryExpirationDate
                  ? formatDateToBritish(
                      entry.jobEntryExpirationDate + "T00:00:00",
                    )
                  : "N/A"}
              </TableCell>
              <TableCell>{entry.salary}</TableCell>
              <TableCell>{entry.salaryUnit}</TableCell>
              <TableCell>{entry.paySchedule}</TableCell>
              <TableCell className="text-center">
                <div className="flex justify-center space-x-2">
                  <Button
                    onClick={() => handleEditEntry(entry)}
                    variant="ghost"
                    size="icon"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button
                    onClick={() => handleRemoveEntry(entry.id)}
                    variant="ghost"
                    size="icon"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="mt-4 flex justify-end space-x-2">
        <Button onClick={handleAddEntry}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add New Entry
        </Button>
        <Button onClick={handleSubmitTimeline}>Submit Timeline</Button>
      </div>

      {/* Working Hours Schedule Component */}
      <WorkingHoursSchedule employeeId={effectiveEmployeeId} />

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Job Entry</DialogTitle>
            <DialogDescription>
              The start date of the first entry in your timeline is the
              employment start date. If you have no end date for the employee,
              there is no need to enter an end date.
              <br />
              <br /> E.g. If my employee started working on 1st February 2024
              and they were paid £60,000 annually, we would have a start date of
              01/02/2024, no end date value, and a salary of £60,000 with a
              salary unit of Yearly.
            </DialogDescription>
          </DialogHeader>
          {editingEntry && (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleUpdateEntry)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                                    name="jobEntryPeriodStartDate"
                  render={({ field }) => (
                    <FormItem>
                                            <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                formatDateToBritish(field.value + "T00:00:00")
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 pointer-events-auto">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(field.value + "T00:00:00")
                                  : undefined
                              }
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              onSelect={(date) => {
                                if (date) {
                                  field.onChange(
                                    date.toISOString().split("T")[0],
                                  );
                                }
                              }}
                               classNames={{
                                  nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                                  caption_label: "text-sm font-medium text-gray-900",
                                  day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                                  day_disabled: "text-gray-600",
                                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                               }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="jobEntryExpirationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                formatDateToBritish(field.value + "T00:00:00")
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 pointer-events-auto">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(field.value + "T00:00:00")
                                  : undefined
                              }
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              onSelect={(date) => {
                                if (date) {
                                  field.onChange(
                                    date.toISOString().split("T")[0],
                                  );
                                }
                              }}
                              classNames={{
                                nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                                caption_label: "text-sm font-medium text-gray-900",
                                day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                                day_disabled: "text-gray-600",
                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="salary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Salary</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={typeof field.value === 'number' ? field.value.toString() : field.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === undefined) {
                              field.onChange(0);
                            } else {
                              const numValue = parseFloat(value);
                              field.onChange(isNaN(numValue) ? 0 : numValue);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="salaryUnit"
                  render={({ field }) => (
                    <FormItem>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <FormLabel>Salary Unit</FormLabel>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              If Pay Schedule is Monthly and Salary Unit is
                              Weekly, the weekly salary will be multiplied by 4
                              to calculate the monthly amount.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select salary unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Yearly">Yearly</SelectItem>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                          <SelectItem value="Hourly">Hourly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paySchedule"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pay Schedule</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select pay schedule" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit">Update</Button>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobInformation;
